import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../lib/AuthContext';
import Button from './ui/Button';
import { AlertTriangle, TrendingUp, X, Zap } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface FreeTierUsage {
    lookupsUsed: number;
    lookupsLimit: number;
    lookupsRemaining: number;
    exportsUsed: number;
    exportsLimit: number;
    exportsRemaining: number;
    bonusLookupsRemaining: number;
    totalLookupsAvailable: number;
    upgradePrompt?: string;
}

interface FreeTierBannerProps {
    /** If provided, overrides the API-fetched usage */
    usage?: FreeTierUsage;
    /** Whether to show the banner persistently or allow dismissal */
    dismissible?: boolean;
}

const FreeTierBanner: React.FC<FreeTierBannerProps> = ({ usage: propUsage, dismissible = true }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [usage, setUsage] = useState<FreeTierUsage | null>(propUsage || null);
    const [dismissed, setDismissed] = useState(false);

    const tier = user?.subscription_tier || 'free';

    useEffect(() => {
        if (propUsage) {
            setUsage(propUsage);
            return;
        }
        fetchUsage();
    }, [propUsage]);

    const fetchUsage = async () => {
        try {
            const token = localStorage.getItem('progeodata_token');
            if (!token || !user) return;

            if (user.subscription_tier && user.subscription_tier !== 'free') return;

            const res = await fetch(`${API_URL}/free-tier/usage`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                const data = await res.json() as any;
                if (data.success && data.tier === 'free') {
                    setUsage(data);
                }
            }
        } catch {
            // Silently fail
        }
    };

    if (tier !== 'free' || !usage || dismissed) return null;

    const usagePercent = Math.round((usage.lookupsUsed / usage.totalLookupsAvailable) * 100);
    const isLow = usage.lookupsRemaining <= 5;
    const isExhausted = usage.lookupsRemaining === 0;

    // Show warning at 60% usage, or always show if exhausted
    if (usagePercent < 60 && !isExhausted && !propUsage) return null;

    return (
        <div className={`relative rounded-xl p-4 mb-6 border ${
            isExhausted
                ? 'bg-red-500/10 border-red-500/30'
                : isLow
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-sky-500/10 border-sky-500/30'
        }`}>
            {dismissible && !isExhausted && (
                <button
                    onClick={() => setDismissed(true)}
                    className="absolute top-3 right-3 text-slate-500 hover:text-slate-300"
                >
                    <X className="w-4 h-4" />
                </button>
            )}

            <div className="flex items-start gap-3">
                {isExhausted ? (
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                ) : isLow ? (
                    <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                ) : (
                    <TrendingUp className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                )}

                <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className={`font-bold text-sm ${
                            isExhausted ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-sky-400'
                        }`}>
                            {isExhausted
                                ? 'Free Lookup Limit Reached'
                                : isLow
                                    ? 'Running Low on Lookups'
                                    : 'Free Tier Usage'}
                        </h4>
                        <span className="text-xs text-slate-400">
                            {usage.lookupsUsed} / {usage.totalLookupsAvailable} lookups used
                        </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                        <div
                            className={`h-2 rounded-full transition-all ${
                                isExhausted
                                    ? 'bg-red-500'
                                    : isLow
                                        ? 'bg-amber-500'
                                        : 'bg-sky-500'
                            }`}
                            style={{ width: `${Math.min(usagePercent, 100)}%` }}
                        />
                    </div>

                    {usage.upgradePrompt && (
                        <p className="text-sm text-slate-300 mb-3">
                            {usage.upgradePrompt}
                        </p>
                    )}

                    {usage.bonusLookupsRemaining > 0 && (
                        <p className="text-xs text-emerald-400 mb-2">
                            Includes {usage.bonusLookupsRemaining} bonus lookups from referrals
                        </p>
                    )}

                    <div className="flex items-center gap-3">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate({ to: '/' }).then(() => {
                                setTimeout(() => {
                                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                            })}
                        >
                            Upgrade to Starter - $99/mo
                        </Button>
                        <span className="text-xs text-slate-500">5,000 lookups/month</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FreeTierBanner;
